import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';

/* ── Helpers ──────────────────────────────────────────────────────────── */
function fmtS(n) {
  return `S/ ${parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// purchase_date/created_at son TIMESTAMP reales (instante UTC) — se proyectan
// a Perú explícitamente (getDate()/getMonth() usarían la zona del proceso/
// navegador, no necesariamente Perú).
function fmtDate(str) {
  if (!str) return '—';
  const d = new Date(str);
  if (isNaN(d)) return '—';
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'America/Lima' });
}

// due_date es DATE pura (sin hora, medianoche UTC) — proyectarla a cualquier
// zona horaria (incluida Perú) la correría un día atrás. Se extrae el día
// calendario directo del string ISO, sin construir un Date.
function fmtDateOnly(str) {
  if (!str) return '—';
  const m = String(str).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(str).slice(0, 10);
}

function fmtNow() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth()+1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fileDateStr() {
  const d = new Date();
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth()+1)}-${p(d.getDate())}`;
}

function statusLabel(r) {
  if (r.status === 'cancelled') return 'ANULADA';
  if (r.vencido && r.status !== 'paid') return 'VENCIDO';
  const map = { pending: 'PENDIENTE', partial: 'PARCIAL', paid: 'PAGADO' };
  return map[r.status] || r.status?.toUpperCase() || '—';
}

function buildRows(data) {
  return data.map((r, i) => ({
    num:        i + 1,
    fecha:      fmtDate(r.purchase_date || r.created_at),
    compra:     r.purchase_number || '—',
    documento:  r.document_type ? `${r.document_type}${r.serie && r.doc_number ? ` ${r.serie}-${r.doc_number}` : ''}` : '—',
    proveedor:  r.provider_name || '—',
    ruc:        r.provider_ruc || '—',
    cuota:      `Cuota ${r.cuota_num}`,
    monto:      parseFloat(r.amount || 0),
    abonado:    parseFloat(r.paid_amount || 0),
    restante:   parseFloat(r.restante || 0),
    vcto:       fmtDateOnly(r.due_date),
    estado:     statusLabel(r),
    isPaid:     r.status === 'paid',
    isVencido:  !!r.vencido && r.status !== 'paid',
  }));
}

/* ──────────────────────────────────────────────────────────────────────
   EXPORTAR PDF
─────────────────────────────────────────────────────────────────────── */
export function exportPDF({ empresa, data, usuario, summary }) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const W     = doc.internal.pageSize.getWidth();
  const navy  = [13, 27, 46];
  const accent= [14, 165, 233];
  const gray  = [100, 116, 139];
  const green = [22, 163, 74];
  const red   = [220, 38, 38];
  const orange= [217, 119, 6];
  const now   = fmtNow();

  // ── Encabezado ──────────────────────────────────────────────────────
  doc.setFillColor(...navy);
  doc.rect(0, 0, W, 22, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(255, 255, 255);
  doc.text(empresa.name || 'PIVASAPOLLOS', 12, 10);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(180, 210, 230);
  doc.text(`RUC: ${empresa.ruc || '20100000001'}  |  ${empresa.address || 'AV. BENAVIDES NRO 6600-6606, MIRAFLORES - LIMA'}`, 12, 16);
  doc.text(`Generado: ${now}  |  Usuario: ${usuario || 'sistema'}`, W - 12, 16, { align: 'right' });

  // ── Título ───────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...navy);
  doc.text('CUENTAS POR PAGAR — GESTIÓN DE DEUDAS A PROVEEDORES', W / 2, 30, { align: 'center' });

  doc.setDrawColor(...accent);
  doc.setLineWidth(0.5);
  doc.line(12, 33, W - 12, 33);

  // ── KPI boxes ────────────────────────────────────────────────────────
  const sumY = 37;
  const boxW = 60;
  const boxes = [
    { label: 'Total Deuda',       value: fmtS(summary?.total_deuda    || 0), color: navy   },
    { label: 'Pendiente de Pago', value: fmtS(summary?.total_pendiente|| 0), color: orange },
    { label: 'Deuda Vencida',     value: fmtS(summary?.total_vencido  || 0), color: red    },
    { label: 'Total Pagado',      value: fmtS(summary?.total_pagado   || 0), color: green  },
  ];
  boxes.forEach((b, i) => {
    const x = 12 + i * (boxW + 3);
    doc.setFillColor(...b.color);
    doc.roundedRect(x, sumY, boxW, 10, 2, 2, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(255, 255, 255);
    doc.text(b.label, x + boxW / 2, sumY + 3.5, { align: 'center' });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(b.value, x + boxW / 2, sumY + 8, { align: 'center' });
  });

  // ── Tabla ─────────────────────────────────────────────────────────────
  const rows = buildRows(data);
  const cols = ['#', 'Fecha', 'N° Compra', 'Documento', 'Proveedor', 'RUC', 'Cuota', 'Monto', 'Abonado', 'Restante', 'Vcto.', 'Estado'];
  const body = rows.map(r => [
    r.num, r.fecha, r.compra, r.documento, r.proveedor, r.ruc, r.cuota,
    fmtS(r.monto), fmtS(r.abonado), fmtS(r.restante), r.vcto, r.estado
  ]);

  autoTable(doc, {
    startY: sumY + 15,
    head: [cols],
    body,
    theme: 'grid',
    styles: {
      fontSize: 7,
      cellPadding: { top: 2, right: 2.5, bottom: 2, left: 2.5 },
      overflow: 'linebreak',
      valign: 'middle',
    },
    headStyles: {
      fillColor: navy,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'center',
      fontSize: 7,
    },
    columnStyles: {
      0:  { halign: 'center', cellWidth: 7   },  // #
      1:  { cellWidth: 18,    halign: 'center' }, // Fecha
      2:  { cellWidth: 22,    fontStyle: 'bold' },// N° Compra
      3:  { cellWidth: 26    },                   // Documento
      4:  { cellWidth: 38,    fontStyle: 'bold' },// Proveedor
      5:  { cellWidth: 22,    halign: 'center' }, // RUC
      6:  { cellWidth: 16,    halign: 'center' }, // Cuota
      7:  { cellWidth: 20,    halign: 'right'  }, // Monto
      8:  { cellWidth: 20,    halign: 'right'  }, // Abonado
      9:  { cellWidth: 22,    halign: 'right', fontStyle: 'bold' }, // Restante
      10: { cellWidth: 18,    halign: 'center' }, // Vcto.
      11: { cellWidth: 18,    halign: 'center' }, // Estado
    },
    didParseCell(data) {
      if (data.section !== 'body') return;
      const r = rows[data.row.index];
      if (!r) return;

      if (r.isPaid) {
        data.cell.styles.fillColor = [240, 253, 244];
      } else if (r.isVencido) {
        data.cell.styles.fillColor = [255, 245, 245];
      } else if (r.estado === 'PARCIAL') {
        data.cell.styles.fillColor = [255, 251, 235];
      }

      // Colorear columna Restante
      if (data.column.index === 9) {
        data.cell.styles.textColor = r.restante > 0 ? red : green;
      }
      // Colorear columna Estado
      if (data.column.index === 11) {
        if (r.estado === 'PAGADO')    data.cell.styles.textColor = green;
        else if (r.estado === 'VENCIDO') data.cell.styles.textColor = red;
        else if (r.estado === 'PARCIAL') data.cell.styles.textColor = orange;
        else                             data.cell.styles.textColor = [220, 120, 38];
      }
    },
    didDrawPage(data) {
      const pageCount = doc.internal.getNumberOfPages();
      const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7);
      doc.setTextColor(...gray);
      doc.text(
        `Página ${currentPage} de ${pageCount}  —  PIVASAPOLLOS  —  ${now}`,
        W / 2,
        doc.internal.pageSize.getHeight() - 5,
        { align: 'center' }
      );
    },
  });

  doc.save(`CxP_${fileDateStr()}.pdf`);
}

/* ──────────────────────────────────────────────────────────────────────
   EXPORTAR EXCEL
─────────────────────────────────────────────────────────────────────── */
export function exportExcel({ empresa, data, usuario, summary }) {
  const wb = XLSX.utils.book_new();
  const ws_data = [];

  const S = {
    title:   { font: { bold: true, sz: 15, color: { rgb: '0D1B2E' } }, alignment: { horizontal: 'center' } },
    sub:     { font: { bold: true, sz: 11, color: { rgb: '0D1B2E' } }, alignment: { horizontal: 'center' } },
    meta:    { font: { sz: 8.5, color: { rgb: '64748B' } }, alignment: { horizontal: 'center' } },
    label:   { font: { bold: true, sz: 9, color: { rgb: '475569' } } },
    hdr:     { font: { bold: true, sz: 9, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '0D1B2E' } }, alignment: { horizontal: 'center', wrapText: true } },
    def:     { font: { sz: 9 } },
    numR:    { font: { sz: 9 }, alignment: { horizontal: 'right' } },
    boldR:   { font: { bold: true, sz: 9 }, alignment: { horizontal: 'right' } },
    center:  { font: { sz: 9 }, alignment: { horizontal: 'center' } },
    paidRow: { font: { sz: 9 }, fill: { fgColor: { rgb: 'F0FDF4' } } },
    overdRow:{ font: { sz: 9 }, fill: { fgColor: { rgb: 'FFF5F5' } } },
    partRow: { font: { sz: 9 }, fill: { fgColor: { rgb: 'FFFBEB' } } },
    restRed: { font: { bold: true, sz: 9, color: { rgb: 'DC2626' } }, alignment: { horizontal: 'right' } },
    restGrn: { font: { bold: true, sz: 9, color: { rgb: '16A34A' } }, alignment: { horizontal: 'right' } },
    sumTotal:{ font: { bold: true, sz: 10, color: { rgb: '0D1B2E' } } },
    sumRed:  { font: { bold: true, sz: 10, color: { rgb: 'DC2626' } } },
    sumGrn:  { font: { bold: true, sz: 10, color: { rgb: '16A34A' } } },
    sumOrg:  { font: { bold: true, sz: 10, color: { rgb: 'D97706' } } },
  };

  const cell = (v, s) => ({ v, t: typeof v === 'number' ? 'n' : 's', s });
  const NCOLS = 12; // columnas en la tabla

  // ── Encabezado empresa ────────────────────────────────────────────────
  ws_data.push([cell(empresa.name || 'PIVASAPOLLOS', S.title)]);
  ws_data.push([cell(`RUC: ${empresa.ruc || '20100000001'}  |  ${empresa.address || 'AV. BENAVIDES NRO 6600-6606, MIRAFLORES - LIMA'}`, S.meta)]);
  ws_data.push([]);
  ws_data.push([cell('CUENTAS POR PAGAR — GESTIÓN DE DEUDAS A PROVEEDORES', S.sub)]);
  ws_data.push([cell(`Generado: ${fmtNow()}  |  Usuario: ${usuario || 'sistema'}`, { font: { sz: 8, italic: true, color: { rgb: '94A3B8' } }, alignment: { horizontal: 'center' } })]);
  ws_data.push([]);

  // ── KPIs ──────────────────────────────────────────────────────────────
  ws_data.push([
    cell('Total Deuda:',       S.label), cell(fmtS(summary?.total_deuda     || 0), S.sumTotal),
    cell(''),
    cell('Pendiente:',         S.label), cell(fmtS(summary?.total_pendiente || 0), S.sumOrg),
    cell(''),
    cell('Vencido:',           S.label), cell(fmtS(summary?.total_vencido   || 0), S.sumRed),
    cell(''),
    cell('Total Pagado:',      S.label), cell(fmtS(summary?.total_pagado    || 0), S.sumGrn),
  ]);
  ws_data.push([]);

  // ── Cabecera de tabla ────────────────────────────────────────────────
  ws_data.push([
    cell('#',          S.hdr),
    cell('Fecha',      S.hdr),
    cell('N° Compra',  S.hdr),
    cell('Documento',  S.hdr),
    cell('Proveedor',  S.hdr),
    cell('RUC',        S.hdr),
    cell('Cuota',      S.hdr),
    cell('Monto',      S.hdr),
    cell('Abonado',    S.hdr),
    cell('Restante',   S.hdr),
    cell('Vcto.',      S.hdr),
    cell('Estado',     S.hdr),
  ]);

  // ── Datos ─────────────────────────────────────────────────────────────
  const rows = buildRows(data);
  rows.forEach(r => {
    let base = S.def;
    if (r.isPaid)     base = S.paidRow;
    else if (r.isVencido) base = S.overdRow;
    else if (r.estado === 'PARCIAL') base = S.partRow;

    const mergeNum  = (s) => ({ ...s, fill: base.fill });
    const restStyle = r.restante > 0 ? { ...S.restRed, fill: base.fill } : { ...S.restGrn, fill: base.fill };

    ws_data.push([
      cell(r.num,       { ...S.center, fill: base.fill }),
      cell(r.fecha,     { ...base, alignment: { horizontal: 'center' } }),
      cell(r.compra,    { font: { bold: true, sz: 9, color: { rgb: '0369A1' } }, fill: base.fill }),
      cell(r.documento, base),
      cell(r.proveedor, { font: { bold: true, sz: 9 }, fill: base.fill }),
      cell(r.ruc,       { ...S.center, fill: base.fill }),
      cell(r.cuota,     { ...S.center, fill: base.fill }),
      cell(r.monto,     mergeNum(S.numR)),
      cell(r.abonado,   { ...mergeNum(S.numR), font: { sz: 9, color: { rgb: '16A34A' } } }),
      cell(r.restante,  restStyle),
      cell(r.vcto,      { ...S.center, fill: base.fill, font: r.isVencido ? { sz: 9, bold: true, color: { rgb: 'DC2626' } } : { sz: 9 } }),
      cell(r.estado,    { ...S.center, fill: base.fill }),
    ]);
  });

  // ── Fila de totales ───────────────────────────────────────────────────
  const totalMonto    = rows.reduce((s, r) => s + r.monto, 0);
  const totalAbonado  = rows.reduce((s, r) => s + r.abonado, 0);
  const totalRestante = rows.reduce((s, r) => s + r.restante, 0);
  ws_data.push([
    cell('', {}), cell('', {}), cell('', {}), cell('', {}), cell('', {}), cell('', {}),
    cell('TOTALES', { font: { bold: true, sz: 9 }, alignment: { horizontal: 'right' } }),
    cell(totalMonto,    { font: { bold: true, sz: 9 }, alignment: { horizontal: 'right' }, fill: { fgColor: { rgb: 'EFF6FF' } } }),
    cell(totalAbonado,  { font: { bold: true, sz: 9, color: { rgb: '16A34A' } }, alignment: { horizontal: 'right' }, fill: { fgColor: { rgb: 'EFF6FF' } } }),
    cell(totalRestante, { font: { bold: true, sz: 9, color: { rgb: 'DC2626' } }, alignment: { horizontal: 'right' }, fill: { fgColor: { rgb: 'EFF6FF' } } }),
    cell('', {}), cell('', {}),
  ]);

  // ── Construir hoja ────────────────────────────────────────────────────
  const ws = XLSX.utils.aoa_to_sheet(ws_data, { skipHeader: true });

  ws['!cols'] = [
    { wpx: 35  }, // #
    { wpx: 80  }, // Fecha
    { wpx: 100 }, // N° Compra
    { wpx: 115 }, // Documento
    { wpx: 160 }, // Proveedor
    { wpx: 95  }, // RUC
    { wpx: 70  }, // Cuota
    { wpx: 80  }, // Monto
    { wpx: 80  }, // Abonado
    { wpx: 85  }, // Restante
    { wpx: 75  }, // Vcto.
    { wpx: 75  }, // Estado
  ];

  ws['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: NCOLS - 1 } },  // nombre empresa
    { s: { r: 1, c: 0 }, e: { r: 1, c: NCOLS - 1 } },  // RUC
    { s: { r: 3, c: 0 }, e: { r: 3, c: NCOLS - 1 } },  // título
    { s: { r: 4, c: 0 }, e: { r: 4, c: NCOLS - 1 } },  // generado
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'CxP');
  XLSX.writeFile(wb, `CxP_${fileDateStr()}.xlsx`);
}
